import style from "./Auth.module.scss";
import { Input, Form as AntdForm, FormProps } from "antd";
import api from "../../app/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HTTP_STATUSES_CODES } from "../../shared/constants";
import { useContext, useState, useEffect } from "react";
import { UserContext, UserContextType } from "../../shared/contexts";
import { setToken, setInn, setFirstName, setLastName, clearAuthData } from "../../shared/app";
import useNotification from "antd/es/notification/useNotification";
import {Button} from '../../components/button/button'
import {LanguageSelector} from '../../components/LanguageSelector'
import { useTranslation } from "../../shared/hooks/useTranslation";

type FieldType = {
  kimlikNo: string;
  lang: string;
};

export function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const { t, language } = useTranslation();

  const { setUser } = useContext<UserContextType>(UserContext);
  const [notificationApi, contextHolder] = useNotification();

  useEffect(() => {
    clearAuthData();
    setUser(null);
  }, [setUser]);

  const onFinish: FormProps<FieldType>["onFinish"] = async (values) => {
    try {
      setIsLoading(true);
      // Авторизация в МИС выполняется на стороне бэкенда — учётные данные МИС
      // не должны попадать во фронтенд-бандл.
      const response = await api.get(
        `/external/token?kimlikNo=${values.kimlikNo}&lang=${language}`
      );

      if (response.status === HTTP_STATUSES_CODES.OK) {
        setToken(response.data.token);
        setInn(values.kimlikNo);

        if (response.data.firstName) setFirstName(response.data.firstName);
        if (response.data.lastName) setLastName(response.data.lastName);

        setUser({
          ...response.data,
          token: response.data.token,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
        });

        const returnTo = searchParams.get("returnTo");
        if (
          returnTo &&
          returnTo.startsWith("/form/") &&
          returnTo.length > 6 &&
          !returnTo.includes("..")
        ) {
          navigate(returnTo, { replace: true });
        } else {
          navigate("/service");
        }
      }
    } catch {
      notificationApi.open({ message: t.errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={style.page}>
      {/* ── Left brand panel ── */}
      <aside className={style.brand}>
        <div className={style.brandContent}>
          <h2 className={style.brandHeading}>{t.authBrandHeading}</h2>
          <p className={style.brandSub}>{t.authBrandSub}</p>

          <div className={style.brandFeatures}>
            <div className={style.brandFeature}>
              <span className={style.brandFeatureIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </span>
              {t.authBrandFeature1}
            </div>
            <div className={style.brandFeature}>
              <span className={style.brandFeatureIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              {t.authBrandFeature2}
            </div>
            <div className={style.brandFeature}>
              <span className={style.brandFeatureIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </span>
              {t.authBrandFeature3}
            </div>
          </div>
        </div>

        <p className={style.brandFooter}>
          © {new Date().getFullYear()} UMC. {t.authCopyright}
        </p>
      </aside>

      {/* ── Right form panel ── */}
      <div className={style.formPanel}>
        {/* Top bar */}
        <header className={style.topBar}>
          <div className={style.topBarLogo}>
            <img src="/logo.svg" alt="UMC" />
          </div>
          <h1 className={style.pageTitle}>{t.title}</h1>
          <div className={style.langWrap}>
            <LanguageSelector variant="compact" />
          </div>
        </header>

        {/* Form body */}
        <main className={style.formBody}>
          <div className={style.card}>
            <div className={style.cardHeader}>
              <div className={style.cardCompact}>
                <h2 className={style.cardTitle}>{t.subtitle}</h2>
                <p className={style.cardDesc}>{t.authCardDesc}</p>
              </div>
            </div>

            <AntdForm name="auth" onFinish={onFinish} autoComplete="off">
              <div className={style.fieldGroup}>
                <AntdForm.Item<FieldType>
                  name="kimlikNo"
                  rules={[
                    { required: true, message: t.requiredField },
                    { pattern: /^\d{12}$/, message: t.iinLengthError },
                  ]}
                  validateTrigger={[]}
                  className={style.item}
                >
                  <Input
                    id="kimlikNo"
                    className={style.input}
                    placeholder={t.iinPlaceholder}
                    size="large"
                    maxLength={12}
                    inputMode="numeric"
                  />
                </AntdForm.Item>
              </div>

              <Button
                htmlType="submit"
                loading={isLoading}
                className={style.button}
              >
                {t.continueButton}
              </Button>
            </AntdForm>

            <p className={style.formFooter}>
              {t.authPrivacyText}{" "}
              <a href="/policy" target="_blank" rel="noopener noreferrer">
                {t.privacyPolicy}
              </a>
            </p>
          </div>
        </main>
      </div>

      {contextHolder}
    </div>
  );
}
